import { Fragment } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import {
  applyIndentToParagraphNode,
  buildBodyParagraphAttrs,
  indentFromNode,
  resolveIndentAboveBlock,
  resolveIndentFromBlockDepth,
} from './post_block_indent.js';
import { BODY_PARAGRAPH_ATTRS } from './post_editor_constants_paragraph.js';
import { POST_EDITOR_MIN_INDENT } from './post_editor_constants.js';

/** True when a paragraph has no visible text (ignores trailing hard breaks). */
export function paragraphIsEffectivelyEmpty(paragraph) {
  if (!paragraph || paragraph.type.name !== 'paragraph') return false;
  if (paragraph.content.size === 0) return true;
  return paragraph.textContent.trim().length === 0;
}

/** Resource table row node types that support title + optional subtitle paragraphs. */
export const RESOURCE_ROW_NODE_TYPES = Object.freeze([
  'downloadRow',
  'externalLinkRow',
  'meetingRow',
  'audioRow',
]);

export function isResourceRowNode(node) {
  return !!node && RESOURCE_ROW_NODE_TYPES.includes(node.type?.name);
}

export function paragraphRowRole(paragraph) {
  return paragraph?.attrs?.psRowRole === 'subtitle' ? 'subtitle' : 'title';
}

/** True when a resource row has an explicit subtitle paragraph. */
export function resourceRowHasSubtitle(rowNode) {
  if (!rowNode) return false;
  let found = false;
  rowNode.forEach((child) => {
    if (child.attrs?.psRowRole === 'subtitle') found = true;
  });
  return found;
}

/** First title paragraph in a row (defaults to first paragraph). */
export function resourceRowTitleParagraph(rowNode) {
  if (!rowNode) return null;
  let title = null;
  rowNode.forEach((child) => {
    if (child.type.name !== 'paragraph') return;
    if (paragraphRowRole(child) === 'subtitle') return;
    if (!title) title = child;
  });
  return title || rowNode.firstChild;
}

export function resourceRowTitleText(rowNode) {
  const paragraph = resourceRowTitleParagraph(rowNode);
  return (paragraph?.textContent || '').trim();
}

export function findResourceRowDepth($pos) {
  for (let d = $pos.depth; d > 0; d--) {
    if (RESOURCE_ROW_NODE_TYPES.includes($pos.node(d).type.name)) return d;
  }
  return -1;
}

export function findListItemDepth($pos) {
  for (let d = $pos.depth; d > 0; d--) {
    if ($pos.node(d).type.name === 'listItem') return d;
  }
  return -1;
}

export function listItemChildStartPos(listItemPos, listItemNode, childIndex) {
  let pos = listItemPos + 1;
  for (let i = 0; i < childIndex; i++) {
    pos += listItemNode.child(i).nodeSize;
  }
  return pos;
}

/** Doc position where `prevNode` starts when it is the immediate previous sibling of $from's block. */
export function posBeforePrevSibling($from, prevNode) {
  return $from.before($from.depth) - prevNode.nodeSize;
}

export function findResourceTableDepth($pos) {
  for (let d = $pos.depth; d >= 0; d--) {
    if ($pos.node(d).type.name === 'resourceTable') return d;
  }
  return -1;
}

/** First title paragraph inside a resource row (works for nested audio/download layouts). */
export function resolveFirstTitleParagraphInRow(row, rowPos) {
  let result = null;
  row.descendants((node, pos) => {
    if (result) return false;
    if (
      node.type.name === 'paragraph' &&
      paragraphRowRole(node) === 'title' &&
      !isResourceRowGapParagraph(node)
    ) {
      result = { node, pos: rowPos + 1 + pos };
      return false;
    }
  });
  return result;
}

export function isInResourceTableInsideList($from) {
  return findListItemDepth($from) >= 0 && findResourceTableDepth($from) >= 0;
}

/**
 * Block list joinBackward / liftListItem when the caret is inside a resource
 * table within a list item (especially at paragraph starts in a row).
 */
export function shouldBlockListBackspaceInResourceTable($from) {
  if (!isInResourceTableInsideList($from)) return false;
  if (isAtResourceRowTitleStart($from)) return true;
  return $from.parent.type.name === 'paragraph' && $from.parentOffset === 0;
}

/** True when the caret is at the start of the first title line in a resource table row. */
export function isAtResourceRowTitleStart($from) {
  const rowDepth = findResourceRowDepth($from);
  if (rowDepth < 0 || findResourceTableDepth($from) < 0) return false;
  if ($from.parent.type.name !== 'paragraph') return false;
  if ($from.parentOffset !== 0) return false;
  if (paragraphRowRole($from.parent) !== 'title') return false;
  if (isResourceRowGapParagraph($from.parent)) return false;

  const rowPos = $from.before(rowDepth);
  const firstTitle = resolveFirstTitleParagraphInRow($from.node(rowDepth), rowPos);
  if (!firstTitle) return false;

  const paraStart = firstTitle.pos;
  const paraEnd = paraStart + firstTitle.node.nodeSize;
  return $from.pos >= paraStart + 1 && $from.pos < paraEnd;
}

/**
 * Index of the resource row containing `$from` within its table.
 * Uses doc positions — `$from.index(rowDepth)` is always 0 for row > paragraph layouts.
 */
export function resourceRowIndexInTable($from, tableDepth) {
  const rowDepth = findResourceRowDepth($from);
  if (rowDepth < 0 || tableDepth < 0 || rowDepth <= tableDepth) return 0;

  const tableNode = $from.node(tableDepth);
  const tablePos = $from.before(tableDepth);
  const rowPos = $from.before(rowDepth);

  let offset = tablePos + 1;
  for (let i = 0; i < tableNode.childCount; i++) {
    const child = tableNode.child(i);
    if (offset === rowPos) return i;
    offset += child.nodeSize;
  }

  const indexAtTableDepth = $from.index(tableDepth);
  if (indexAtTableDepth >= 0 && indexAtTableDepth < tableNode.childCount) {
    return indexAtTableDepth;
  }

  return 0;
}

/** Doc position of the first paragraph node in a block fragment inserted at `startPos`. */
export function firstParagraphPosInBlockFragment(startPos, nodes) {
  let pos = startPos;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node.type.name === 'paragraph') return pos;
    pos += node.nodeSize;
  }
  return startPos;
}

/**
 * Top-to-bottom indent for content unwrapped from a resource table: prefer the first
 * paragraph/block above the table; when none, use the table's own indent (sibling context).
 */
export function resolveIndentAboveResourceTable($from, tableDepth) {
  if (tableDepth < 0) return POST_EDITOR_MIN_INDENT;

  const above = resolveIndentAboveBlock($from, tableDepth);
  if (above > POST_EDITOR_MIN_INDENT) return above;

  const before = paragraphBeforeBlock($from, tableDepth);
  if (before?.node) {
    const fromParagraph = indentFromNode(before.node);
    if (fromParagraph > POST_EDITOR_MIN_INDENT) return fromParagraph;
  }

  return resolveIndentFromBlockDepth($from, tableDepth);
}

/**
 * Indent for an unwrapped row. Middle/last rows (split) keep the table's indent;
 * first/only rows use top-to-bottom context above the table.
 */
export function resolveIndentForResourceTableRowUnwrap($from, tableDepth, tableNode, rowIndex) {
  const contextIndent = resolveIndentAboveResourceTable($from, tableDepth);
  const tableIndent = indentFromNode(tableNode);
  const splitAtRow = tableNode.childCount > 1 && rowIndex > 0;
  if (!splitAtRow) return contextIndent;
  return Math.max(tableIndent, contextIndent);
}

/** Paragraph immediately before a block (e.g. resource table) within the same parent. */
export function paragraphBeforeBlock($from, blockDepth) {
  const parentDepth = blockDepth - 1;
  if (parentDepth < 0) return null;
  const blockIndex = $from.index(blockDepth);
  if (blockIndex <= 0) return null;
  const parent = $from.node(parentDepth);
  const prev = parent.child(blockIndex - 1);
  if (prev.type.name !== 'paragraph') return null;
  // Doc-level blocks: parent is the doc node — `$from.before(0)` throws.
  const parentPos = parentDepth === 0 ? 0 : $from.before(parentDepth);
  return {
    node: prev,
    pos: listItemChildStartPos(parentPos, parent, blockIndex - 1),
  };
}

/**
 * Where to insert a new resource row: after the row containing the cursor,
 * or at the end of the resource table. Uses `before()` so positions are not
 * mistaken for the first row when the cursor is in a later row (e.g. audio).
 */
export function resolveResourceTableRowInsert(state) {
  const { $from } = state.selection;
  const tableDepth = findResourceTableDepth($from);
  if (tableDepth < 0) return null;

  const tablePos = $from.before(tableDepth);
  const tableNode = state.doc.nodeAt(tablePos);
  if (!tableNode) return null;

  const rowDepth = findResourceRowDepth($from);
  let insertPos;
  if (rowDepth >= 0) {
    const rowPos = $from.before(rowDepth);
    const rowNode = state.doc.nodeAt(rowPos);
    insertPos = rowNode ? rowPos + rowNode.nodeSize : tablePos + tableNode.nodeSize - 1;
  } else {
    insertPos = tablePos + tableNode.nodeSize - 1;
  }

  return { tableDepth, tablePos, insertPos };
}

/** Doc position at the end of the title paragraph in a resource row. */
export function resourceRowTitleEndAtRowPos(doc, rowPos) {
  const rowNode = doc.nodeAt(rowPos);
  if (!rowNode || !isResourceRowNode(rowNode)) return null;
  const firstTitle = resolveFirstTitleParagraphInRow(rowNode, rowPos);
  if (!firstTitle) return rowPos + 1;
  return paragraphTextEndPos(firstTitle.pos, firstTitle.node);
}

/** Selection at the end of the title text for a row matching `predicate` in a table. */
export function resourceRowTitleTextEndSelectionPos(doc, tablePos, predicate) {
  const tableNode = doc.nodeAt(tablePos);
  if (!tableNode || tableNode.type.name !== 'resourceTable') return null;

  let offset = 1;
  for (let i = 0; i < tableNode.childCount; i++) {
    const row = tableNode.child(i);
    if (predicate(row)) {
      return resourceRowTitleEndAtRowPos(doc, tablePos + offset);
    }
    offset += row.nodeSize;
  }
  return null;
}

/** @deprecated Use resourceRowTitleTextEndSelectionPos — kept as alias for title end. */
export function resourceRowTitleSelectionPos(doc, tablePos, predicate) {
  return resourceRowTitleTextEndSelectionPos(doc, tablePos, predicate);
}

/** Title text end for the resource row at/near an insert position (list or body). */
export function resourceRowTitleEndNearInsertPos(doc, insertPos) {
  const bounded = Math.max(1, Math.min(insertPos, doc.content.size - 1));
  const $pos = doc.resolve(bounded);

  for (let d = $pos.depth; d > 0; d--) {
    if (isResourceRowNode($pos.node(d))) {
      return resourceRowTitleEndAtRowPos(doc, $pos.before(d));
    }
  }

  if ($pos.nodeAfter && isResourceRowNode($pos.nodeAfter)) {
    return resourceRowTitleEndAtRowPos(doc, bounded);
  }

  if ($pos.nodeBefore && isResourceRowNode($pos.nodeBefore)) {
    return resourceRowTitleEndAtRowPos(doc, bounded - $pos.nodeBefore.nodeSize);
  }

  return null;
}

/** Move the cursor to the end of a newly inserted row's title (returns false if not found). */
export function focusResourceRowTitleTextEnd(editor, options = {}) {
  if (!editor || editor.isDestroyed) return false;

  const { tablePos, predicate, insertPos, resourceId, rowType } = options;
  const doc = editor.state.doc;

  let endPos = null;
  const rowPredicate =
    predicate ||
    (resourceId != null && rowType
      ? (row) =>
          row.type.name === rowType && String(row.attrs.resourceId) === String(resourceId)
      : null);

  if (tablePos != null && rowPredicate) {
    endPos = resourceRowTitleTextEndSelectionPos(doc, tablePos, rowPredicate);
  }
  if (endPos == null && insertPos != null) {
    endPos = resourceRowTitleEndNearInsertPos(doc, insertPos);
  }

  if (endPos == null) return false;

  try {
    editor.chain().focus().setTextSelection(endPos).run();
  } catch {
    const safe = Math.max(1, Math.min(endPos, doc.content.size - 1));
    editor.chain().focus().setTextSelection(TextSelection.near(doc.resolve(safe), -1)).run();
  }
  return true;
}

/** Retry focus after DOM/schema updates following row insert. */
export function scheduleFocusInsertedResourceRowTitleEnd(editor, options = {}, { maxAttempts = 5 } = {}) {
  let attempts = 0;
  const tryMove = () => {
    if (editor.isDestroyed) return;
    attempts++;
    if (!focusResourceRowTitleTextEnd(editor, options) && attempts < maxAttempts) {
      setTimeout(tryMove, 50 * attempts);
    }
  };
  setTimeout(tryMove, 50);
}

/** Call after insertContent for a resource row (toolbar, extensions, upload handlers). */
export function afterResourceRowInsertFocus(editor, state, opts = {}) {
  if (!editor || editor.isDestroyed) return;
  const { resourceId, rowType, insertCtx } = opts;
  scheduleFocusInsertedResourceRowTitleEnd(editor, {
    tablePos: opts.tablePos ?? insertCtx?.tablePos,
    insertPos: opts.insertPos ?? insertCtx?.insertPos ?? state?.selection?.from,
    resourceId,
    rowType,
  });
}

export function rowChildStartPos(rowPos, rowNode, childIndex) {
  let pos = rowPos + 1;
  for (let i = 0; i < childIndex; i++) {
    pos += rowNode.child(i).nodeSize;
  }
  return pos;
}

/** Doc position where a new subtitle paragraph should be inserted at the end of a row. */
export function resourceRowSubtitleInsertPos(rowPos, rowNode) {
  return rowPos + 1 + rowNode.content.size;
}

/** Cursor position inside the subtitle paragraph of a resource row. */
export function resourceRowSubtitleTextPos(doc, rowPos) {
  const rowNode = doc.nodeAt(rowPos);
  if (!rowNode || !isResourceRowNode(rowNode)) return null;
  const subtitleIndex = findSubtitleChildIndex(rowNode);
  if (subtitleIndex < 0) return null;
  return rowChildStartPos(rowPos, rowNode, subtitleIndex) + 1;
}

export const RESOURCE_ROW_SUBTITLE_ADDED_META = 'resourceRowSubtitleAdded';

export function findSubtitleChildIndex(rowNode) {
  let index = -1;
  rowNode.forEach((child, _offset, i) => {
    if (paragraphRowRole(child) === 'subtitle') index = i;
  });
  return index;
}

/** Index of the first subtitle paragraph in a resource row. */
export function firstSubtitleChildIndex(rowNode) {
  let index = -1;
  rowNode.forEach((child, _offset, i) => {
    if (index < 0 && paragraphRowRole(child) === 'subtitle') index = i;
  });
  return index;
}

export function lastTitleChildIndex(rowNode) {
  let lastIndex = 0;
  rowNode.forEach((child, _offset, index) => {
    if (paragraphRowRole(child) === 'title') lastIndex = index;
  });
  return lastIndex;
}

export const RESOURCE_ROW_TITLE_PARAGRAPH_ATTRS = { psRowRole: 'title' };
export const RESOURCE_ROW_SUBTITLE_PARAGRAPH_ATTRS = { psRowRole: 'subtitle' };
/** Empty title line inserted on purpose between title and subtitle (Enter at end of title). */
export const RESOURCE_ROW_GAP_PARAGRAPH_ATTRS = {
  psRowRole: 'title',
  psRowGap: true,
};

export function isResourceRowGapParagraph(paragraph) {
  return !!paragraph?.attrs?.psRowGap;
}

/**
 * Drop empty title lines between title and subtitle left over from exit — not the
 * double-Enter staging line (no subtitle yet) or intentional gaps (`data-ps-row-gap`).
 */
export function buildSanitizedResourceRowContent(rowNode) {
  if (!rowNode || !isResourceRowNode(rowNode)) return null;

  const hasSubtitle = resourceRowHasSubtitle(rowNode);
  if (!hasSubtitle) return null;

  const kept = [];
  let changed = false;
  rowNode.forEach((child) => {
    if (
      child.type.name === 'paragraph' &&
      paragraphRowRole(child) !== 'subtitle' &&
      paragraphIsEffectivelyEmpty(child) &&
      !isResourceRowGapParagraph(child)
    ) {
      changed = true;
      return;
    }
    kept.push(child);
  });

  if (!changed) return null;
  return Fragment.from(kept);
}

/** Remove empty title paragraphs inside resource rows. Returns true when `tr` changed. */
export function sanitizeResourceRows(tr, doc) {
  let modified = false;

  doc.descendants((node, pos) => {
    if (!isResourceRowNode(node)) return;

    const sanitized = buildSanitizedResourceRowContent(node);
    if (!sanitized) return;

    tr.replaceWith(pos + 1, pos + 1 + node.content.size, sanitized);
    modified = true;
  });

  return modified;
}

/** Doc position at the end of inline content inside a paragraph node. */
export function paragraphTextEndPos(paragraphPos, paragraphNode) {
  return paragraphPos + Math.max(1, paragraphNode.nodeSize - 1);
}

/** Place the cursor at the end of inline content in a paragraph at `paragraphPos`. */
export function setSelectionAtParagraphContentEnd(tr, paragraphPos) {
  const paragraph = tr.doc.nodeAt(paragraphPos);
  if (!paragraph || paragraph.type.name !== 'paragraph') {
    const safe = Math.max(1, Math.min(paragraphPos + 1, tr.doc.content.size - 1));
    return tr.setSelection(TextSelection.near(tr.doc.resolve(safe), -1));
  }
  const endPos = paragraphTextEndPos(paragraphPos, paragraph);
  try {
    return tr.setSelection(TextSelection.create(tr.doc, endPos));
  } catch {
    return tr.setSelection(TextSelection.near(tr.doc.resolve(endPos), -1));
  }
}

/**
 * Backspace at the start of a body paragraph below a blockquote / resource row:
 * merge into the target paragraph and place the cursor at the end of that text.
 */
export function mergeBackspaceFromParagraphBelow(editor, $from, targetParagraphPos, targetParagraph) {
  const insertPos = paragraphTextEndPos(targetParagraphPos, targetParagraph);
  const deleteFrom = $from.before($from.depth);
  const deleteTo = $from.after($from.depth);
  const incomingContent = $from.parent.content;

  return editor
    .chain()
    .focus()
    .command(({ tr, dispatch }) => {
      if (incomingContent.size === 0) {
        tr.delete(deleteFrom, deleteTo);
        const cursorPos = tr.mapping.map(insertPos, -1);
        tr.setSelection(TextSelection.create(tr.doc, cursorPos));
      } else {
        tr.insert(insertPos, incomingContent);
        tr.delete(tr.mapping.map(deleteFrom), tr.mapping.map(deleteTo));
        const cursorPos = tr.mapping.map(insertPos) + incomingContent.size;
        tr.setSelection(TextSelection.create(tr.doc, cursorPos));
      }
      if (dispatch) dispatch(tr);
      return true;
    })
    .run();
}

/**
 * Split a resource table at `rowIndex`: table(rows before) + body paragraph + table(rows after).
 * Same in list items and body; never merges into a paragraph above the table.
 */
function splitResourceTableAtRow(tr, tablePos, tableNode, rowIndex, tableType, unwrappedPara, indentLevel) {
  const tableAttrs = { ...tableNode.attrs, indent: indentLevel };
  const rowsBefore = [];
  for (let i = 0; i < rowIndex; i++) {
    rowsBefore.push(tableNode.child(i));
  }
  const rowsAfter = [];
  for (let i = rowIndex + 1; i < tableNode.childCount; i++) {
    rowsAfter.push(tableNode.child(i));
  }

  const replacement = [];
  if (rowsBefore.length > 0) {
    replacement.push(tableType.create(tableAttrs, Fragment.from(rowsBefore)));
  }
  replacement.push(unwrappedPara);
  if (rowsAfter.length > 0) {
    replacement.push(tableType.create(tableAttrs, Fragment.from(rowsAfter)));
  }

  const mappedTableFrom = tablePos;
  const mappedTableTo = tablePos + tableNode.nodeSize;
  tr = tr.replaceWith(mappedTableFrom, mappedTableTo, Fragment.from(replacement));

  return {
    tr,
    selectionParagraphPos: firstParagraphPosInBlockFragment(mappedTableFrom, replacement),
  };
}

/**
 * Backspace at the start of a resource-row title (list or body):
 * - 1 row + paragraph above: merge into that paragraph, remove table.
 * - 1 row, no paragraph above: replace table with title paragraph.
 * - Row 1 of 2+ (index 0): merge into paragraph above, or prepend title + shrunk table.
 * - Row 2+ (index > 0): always split table + unwrapped paragraph + table; keep table indent.
 */
export function unwrapResourceRowAtTitleStart(editor, $from) {
  if (!isAtResourceRowTitleStart($from)) return false;

  const { state, view } = editor;
  const tableDepth = findResourceTableDepth($from);
  const tablePos = $from.before(tableDepth);
  const tableNode = $from.node(tableDepth);
  const rowIndex = resourceRowIndexInTable($from, tableDepth);
  const incomingContent = $from.parent.content;
  const $atTable = state.doc.resolve(tablePos + 1);
  const tableType = state.schema.nodes.resourceTable;
  const paragraphType = state.schema.nodes.paragraph;
  if (!tableType || !paragraphType) return false;

  const inheritedIndent = resolveIndentForResourceTableRowUnwrap(
    $atTable,
    tableDepth,
    tableNode,
    rowIndex
  );
  const tableAttrsWithIndent = { ...tableNode.attrs, indent: inheritedIndent };
  let tr = state.tr;
  const mappedTableFrom = tablePos;
  const mappedTableTo = tablePos + tableNode.nodeSize;
  const unwrappedPara = paragraphType.create(
    buildBodyParagraphAttrs(inheritedIndent),
    incomingContent
  );
  let selectionParagraphPos = null;

  // Middle or last row in a multi-row table: always split (list or body).
  if (tableNode.childCount > 1 && rowIndex > 0) {
    const split = splitResourceTableAtRow(
      tr,
      mappedTableFrom,
      tableNode,
      rowIndex,
      tableType,
      unwrappedPara,
      inheritedIndent
    );
    tr = split.tr;
    selectionParagraphPos = split.selectionParagraphPos;
  } else if (tableNode.childCount === 1) {
    const mergeTarget = paragraphBeforeBlock($atTable, tableDepth);
    if (mergeTarget) {
      const insertPos = paragraphTextEndPos(mergeTarget.pos, mergeTarget.node);
      if (incomingContent.size > 0) {
        tr = tr.insert(insertPos, incomingContent);
      }
      tr = tr.delete(mappedTableFrom, mappedTableTo);
      selectionParagraphPos = tr.mapping.map(mergeTarget.pos);
    } else {
      tr = tr.replaceWith(mappedTableFrom, mappedTableTo, unwrappedPara);
      selectionParagraphPos = mappedTableFrom;
    }
  } else {
    const mergeTarget = paragraphBeforeBlock($atTable, tableDepth);
    const remainingRows = [];
    for (let i = 1; i < tableNode.childCount; i++) {
      remainingRows.push(tableNode.child(i));
    }
    const shrunkTable = tableType.create(tableAttrsWithIndent, Fragment.from(remainingRows));

    if (mergeTarget) {
      const insertPos = paragraphTextEndPos(mergeTarget.pos, mergeTarget.node);
      if (incomingContent.size > 0) {
        tr = tr.insert(insertPos, incomingContent);
      }
      tr = tr.replaceWith(
        tr.mapping.map(mappedTableFrom),
        tr.mapping.map(mappedTableTo),
        shrunkTable
      );
      selectionParagraphPos = tr.mapping.map(mergeTarget.pos);
    } else {
      tr = tr.replaceWith(
        mappedTableFrom,
        mappedTableTo,
        Fragment.from([unwrappedPara, shrunkTable])
      );
      selectionParagraphPos = mappedTableFrom;
    }
  }

  if (inheritedIndent > POST_EDITOR_MIN_INDENT && selectionParagraphPos != null) {
    const mappedSelectionPos = tr.mapping.map(selectionParagraphPos, -1);
    tr = applyIndentToParagraphNode(tr, mappedSelectionPos, inheritedIndent);
    selectionParagraphPos = mappedSelectionPos;
  }

  tr = setSelectionAtParagraphContentEnd(tr, selectionParagraphPos);

  view.dispatch(tr.scrollIntoView());
  return true;
}
